import { useFlags } from 'src/feature-flags/FlagsHook';
import type { MeetingNoteStaleness } from 'src/meeting-note/types';
import { MeetingBanner } from 'src/meetings/detail/components/MeetingBanner';
import { useRegenerateMeetingNote } from 'src/meetings/hooks/useRegenerateMeetingNote';
import type { CalendarEvent } from 'src/meetings/models';

// Profile updates go out of date on a speaker change only: detection attributes each fact to whoever
// the transcript shows saying it.
export type OutOfDateProfileUpdates = {
  regenerate: () => Promise<void>;
  isRegenerating: boolean;
};

type Props = {
  event: CalendarEvent;
  // Only the new meeting page offers profile updates, so only it passes them.
  profileUpdates?: OutOfDateProfileUpdates;
};

type StaleOutputs = 'NOTE' | 'PROFILE_UPDATES' | 'BOTH';

const TITLE: Record<MeetingNoteStaleness, string> = {
  ATTENDEES: 'Attendees have changed',
  SPEAKERS: 'Speakers have changed',
  ATTENDEES_AND_SPEAKERS: 'Attendees and speakers have changed',
};

const OUTPUTS: Record<StaleOutputs, string> = {
  NOTE: 'the meeting note',
  PROFILE_UPDATES: 'the suggested profile updates',
  BOTH: 'the meeting note and suggested profile updates',
};

// Naming the wrong people and putting a statement in the wrong mouth are different mistakes, and an
// adviser checking the outputs needs to know which one to look for, so each reason gets its own wording.
const CONSEQUENCE: Record<MeetingNoteStaleness, string> = {
  ATTENDEES: 'name the wrong people',
  SPEAKERS: 'attribute statements to the wrong person',
  ATTENDEES_AND_SPEAKERS: 'name the wrong people and attribute statements to the wrong person',
};

const ACTION: Record<StaleOutputs, string> = {
  NOTE: 'Regenerate the note to update it.',
  PROFILE_UPDATES: 'Reprocess the meeting to update them.',
  BOTH: 'Reprocessing the meeting regenerates both.',
};

function combined(
  noteReason: MeetingNoteStaleness | undefined,
  speakersChanged: boolean,
): MeetingNoteStaleness {
  const attendees = noteReason !== undefined && noteReason !== 'SPEAKERS';
  const speakers = speakersChanged || (noteReason !== undefined && noteReason !== 'ATTENDEES');
  if (attendees && speakers) return 'ATTENDEES_AND_SPEAKERS';
  return attendees ? 'ATTENDEES' : 'SPEAKERS';
}

function staleOutputs(noteStale: boolean, profileUpdatesStale: boolean): StaleOutputs {
  if (noteStale && profileUpdatesStale) return 'BOTH';
  return noteStale ? 'NOTE' : 'PROFILE_UPDATES';
}

/**
 * Warns that the meeting note, the suggested profile updates, or both were generated before the
 * attendees or the speakers changed, and reprocesses whichever is out of date in one go.
 */
export function MeetingOutOfDateBanner({ event, profileUpdates }: Props) {
  const { flagEnabledAttendeeChangesRefreshOutputs, flagEnabledMeetingsRework } = useFlags();
  const {
    meetingNote,
    canRegenerate,
    regenerate: regenerateNote,
    isRegenerating: isRegeneratingNote,
  } = useRegenerateMeetingNote(event);

  const anyFlagOn = flagEnabledAttendeeChangesRefreshOutputs || flagEnabledMeetingsRework;
  const noteStale = anyFlagOn && !!meetingNote?.isOutOfDate && canRegenerate;
  if (!noteStale && !profileUpdates) {
    return null;
  }

  // A current API always sends the reason alongside isOutOfDate, so this falls back only for a website released
  // ahead of the API, which reports staleness without saying why — and only ever meant the attendees.
  const reason = combined(
    noteStale ? (meetingNote?.outOfDateReason ?? 'ATTENDEES') : undefined,
    !!profileUpdates,
  );
  const outputs = staleOutputs(noteStale, !!profileUpdates);

  const reprocess = async () => {
    await Promise.all([
      ...(noteStale ? [regenerateNote()] : []),
      ...(profileUpdates ? [profileUpdates.regenerate()] : []),
    ]);
  };

  return (
    <MeetingBanner
      title={TITLE[reason]}
      mb="md"
      action={{
        label: outputs === 'NOTE' ? 'Regenerate meeting note' : 'Reprocess meeting',
        onClick: () => void reprocess(),
        loading: isRegeneratingNote || !!profileUpdates?.isRegenerating,
      }}
    >
      {`Because of this, ${OUTPUTS[outputs]} may ${CONSEQUENCE[reason]}. ${ACTION[outputs]}`}
    </MeetingBanner>
  );
}
